import { Box, Typography } from "@mui/material";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { MetaChip } from "../Badges/MetaChip";

/** A related topic offered as a tappable follow-up. `id` is the stable key; `title` is what's shown. */
export interface ChatSuggestionItem {
	id: string;
	title: string;
}

type Props<Item extends ChatSuggestionItem> = {
	/** Already-translated lead-in, e.g. "Related". */
	label: string;
	suggestions: Item[];
	/** Invoked when a chip is tapped. */
	onSelect: (suggestion: Item) => void;
	/** Accessible name for a chip given its title (describes the action, e.g. "Tell me about X"). */
	ariaLabel: (title: string) => string;
	/** Show the choices as read-only while a turn runs or after its choice window closes. */
	disabled?: boolean;
	dataTestId?: string;
};

/**
 * Follow-up chips for a grounded chat answer: a muted lead-in plus one primary-tinted, tappable chip
 * per related topic. Distinct from `ChatSources` (read-only, neutral) — these are actions that ask the
 * assistant about the topic next. Renders nothing when there are no suggestions.
 */
export const ChatSuggestions = <Item extends ChatSuggestionItem>({ label, suggestions, onSelect, ariaLabel, disabled, dataTestId }: Props<Item>) => {
	if (suggestions.length === 0) return null;
	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 2 }}>
			<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
				{label}
			</Typography>
			{suggestions.map((suggestion) => (
				<MetaChip
					key={suggestion.id}
					label={suggestion.title}
					tone={disabled ? "neutral" : "primary"}
					size="small"
					onClick={disabled ? undefined : () => onSelect(suggestion)}
					ariaLabel={ariaLabel(suggestion.title)}
					dataTestId={`chat-suggestion-${suggestion.id}`}
				/>
			))}
		</Box>
	);
};
